<template>
  <multipane>
    <el-table
        :data="dictionaryView"
        row-key="id"
        :height="height"
        :style="{width: '30%', maxWidth: '500px', flex: 'none'}"
        ref="dictionaryTable"
        @current-change="handleDictionarySelectionChange"
        highlight-current-row>
      <el-table-column
          label="個別辞書名称"
          prop="name">
        <template #default="{row}">
          <table-inline>
            <dictionary-name-field :value="row"></dictionary-name-field>
          </table-inline>
        </template>
      </el-table-column>
    </el-table>
    <multipane-resizer></multipane-resizer>
    <el-table
        :data="dataComponentModelView"
        row-key="id"
        :empty-text="selectedDictionary ?
          '選択した辞書にはデータ構成モデルが存在しません' :
          '個別辞書を選択すると、こちら側にデータ構成モデルが表示されます'
        "
        :height="height"
        @current-change="handleSelectionChange"
        ref="dcmTable"
        highlight-current-row>
      <el-table-column
          label="名称"
          prop="name"
          width="200">
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.name">
              <template #prepend>
                <update-icon
                    :type="row.updateType"
                    class="mt-1"
                    small>
                </update-icon>
              </template>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="description">
        <template #default="{row}">
          <table-inline>
            <description-input
                :value="row.description"
                readonly>
            </description-input>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="100"
          label="登録日"
          prop="createdAt">
        <template #default="{row}">
          {{ formatDate(row.createdAt) }}
        </template>
      </el-table-column>
    </el-table>
  </multipane>
</template>

<script>
import {Multipane, MultipaneResizer} from "vue-multipane";
import {mapGetters} from "vuex";
import gql from "graphql-tag";
import TableInline from "@/components/atoms/TableInline.vue";
import DescriptionInput from "@/components/molecules/DescriptionInput.vue";
import natsort from "natsort";
import Formatter from "@/utils/Formatter";
import DictionaryNameField from "@/components/molecules/DictionaryNameField.vue";
import IdInfo from "@/store/modules/hds/IdInfo";
import UpdateIcon from "@/components/molecules/UpdateIcon.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";

const sortFn = natsort();
export default {
  name: 'DataComponentModelTable',
  mixins: [
    Formatter
  ],
  components: {
    TextFieldLike,
    UpdateIcon,
    DictionaryNameField,
    DescriptionInput,
    TableInline,
    Multipane,
    MultipaneResizer
  },
  inject: {
    form: {default: null},
    dialog: {default: null},
  },
  props: {
    value: {
      type: String,
      default: null
    },
    existingDataImplementations: {
      type: Array,
      default: () => ([])
    },
    selectedControllerId: {
      type: String,
      default: null
    },
    height: {
      type: [Number, String],
    },
  },
  data() {
    return {
      dictionaries: [],
      selectedDictionary: null,
      selectedDataComponentModel: null
    };
  },
  created() {
    this.form && this.form.register(this);
    this.client
        .query({
          query: gql`
            query DataComponentModelTable {
                dictionary: dictionariesByTypes(dictionaryTypes: [local, external]) {
                    id
                    name
                    description
                    dictionaryType
                    dataComponentModels {
                        id
                        name
                        description
                        createdAt
                        dataTranslationMaps {
                            targetDataComponentModelId
                        }
                    }
                }
            }`
        })
        .then(response => {
          this.dictionaries = response.data.dictionary;
        });
  },
  beforeDestroy() {
    this.form && this.form.unregister(this)
  },
  computed: {
    ...mapGetters({
      client: 'hds/gqlClient',
    }),
    dictionaryView() {
      return this.dictionaries
          .slice()
          .sort((a, b) => sortFn(a.name, b.name));
    },
    dataComponentModelView() {
      if (this.selectedDictionary) {
        return this.selectedDictionary.dataComponentModels
            .filter(dcm => dcm.dataTranslationMaps.length > 0)
            .sort((a, b) => sortFn(a.name, b.name))
            .map(dcm => this.buildDataComponentModelView(dcm));
      } else {
        return [];
      }
    },
    hasError() {
      if(this.selectedDataComponentModel) {
        return this.selectedDataComponentModel.updateType === 'stay';
      }
      return true;
    },
    existingDataImplementationsView() {
      return this.existingDataImplementations.map(di => ({
        ...di,
        idInfo: IdInfo.of(di.dataComponentModelId)
      }));
    },
  },
  methods: {
    buildDataComponentModelView(dcm) {
      if(!dcm) {
        return null;
      }
      const dcmIdInfo = IdInfo.of(dcm.id);
      const existingIdInfo = this.existingDataImplementationsView.find(di =>
          di.controllerId === this.selectedControllerId &&
          di.idInfo.elementId === dcmIdInfo.elementId);
      return {
        ...dcm,
        updateType: (() => {
          if(!existingIdInfo) {
            return 'add';
          } else if(existingIdInfo.idInfo.version === dcmIdInfo.version) {
            return 'stay';
          } else {
            return 'update';
          }
        })(),
      };
    },
    handleDictionarySelectionChange(row) {
      this.selectedDictionary = row;
      this.selectedDataComponentModel = null;
      this.$refs.dcmTable?.setCurrentRow();
    },
    handleSelectionChange(row) {
      this.selectedDataComponentModel = row;
      this.$emit('input', row?.id ?? null);
      this.$emit('select', row);
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if((this.selectedDataComponentModel?.id ?? null) === (value ?? null)) {
          return;
        }
        this.selectedDictionary = null;
        this.selectedDataComponentModel = null;
        for(const d of this.dictionaries) {
          const dcm = d.dataComponentModels?.find(dcm => dcm.id === value);
          if(dcm) {
            this.selectedDictionary = d;
            this.selectedDataComponentModel = this.buildDataComponentModelView(dcm);
          }
        }
      }
    },
    existingDataImplementations: {
      handler() {
        if(this.selectedDataComponentModel) {
          this.selectedDataComponentModel = this.buildDataComponentModelView(this.selectedDataComponentModel);
        }
      }
    },
    'dialog.visible': {
      handler(visible) {
        if(!visible) {
          this.selectedDictionary = null;
          this.$refs.dictionaryTable?.setCurrentRow();
          this.$refs.dcmTable?.setCurrentRow();
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .el-table__empty-text {
  line-height: normal;
  width: 100%;
}
</style>
